// Shared raster ops over palette-indexed canvases.
//
// Two consumers depend on the *exact* same algorithms here:
//   - saveReference.js quantizes + segments the author's traced drawing
//   - scoring.js quantizes + segments the player's drawing
// The match check is meaningful only if both sides snap colors and group
// pixels identically — diverging implementations would silently make
// scores depend on which code path quantized last. One source of truth.

import { hexToRgb } from "./colorUtils";
import { QUANTIZE_ALPHA_THRESHOLD } from "./constants";

// Sentinel palette index for "this pixel is empty" — well outside the
// 0..6 range a real palette ever uses. Both quantize and flood-fill treat
// it as the "skip" marker.
export const TRANSPARENT_INDEX = 255;

export function nearestPaletteIndex(r, g, b, paletteRgb) {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < paletteRgb.length; i++) {
    const p = paletteRgb[i];
    const dr = r - p.r;
    const dg = g - p.g;
    const db = b - p.b;
    const d = dr * dr + dg * dg + db * db;
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

// Snap each canvas pixel to its nearest palette slot. Pixels under the
// alpha threshold become TRANSPARENT_INDEX. Anti-aliased stroke edges
// get crisp palette indices — required for connected-components
// grouping to produce sensible regions.
//
// `paletteRgb` is the precomputed `palette.map(hexToRgb)` form so callers
// can hoist the conversion when iterating multiple times. Use
// `quantizeCanvas` for the simple hex-palette path.
export function quantizeImageData(imageData, paletteRgb) {
  const { data, width, height } = imageData;
  const indices = new Uint8Array(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    if (data[i + 3] < QUANTIZE_ALPHA_THRESHOLD) {
      indices[p] = TRANSPARENT_INDEX;
    } else {
      indices[p] = nearestPaletteIndex(
        data[i],
        data[i + 1],
        data[i + 2],
        paletteRgb,
      );
    }
  }
  return { indices, width, height };
}

export function quantizeCanvas(canvas, palette) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const paletteRgb = palette.map(hexToRgb);
  return quantizeImageData(imageData, paletteRgb);
}

// 4-neighbor connected-components over a palette-indexed buffer. Returns
// one entry per connected blob of same-color pixels (skipping
// TRANSPARENT_INDEX), each carrying its colorIndex, bbox in pixel coords,
// and the flat global indices of the pixels that belong to it.
export function findConnectedComponents(indices, width, height) {
  const visited = new Uint8Array(indices.length);
  const components = [];
  const stack = [];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const seed = y * width + x;
      if (visited[seed]) continue;
      const color = indices[seed];
      if (color === TRANSPARENT_INDEX) continue;

      stack.length = 0;
      stack.push(seed);
      let minX = x;
      let maxX = x;
      let minY = y;
      let maxY = y;
      const pixels = [];

      while (stack.length > 0) {
        const i = stack.pop();
        if (visited[i]) continue;
        if (indices[i] !== color) continue;
        visited[i] = 1;
        pixels.push(i);

        const cx = i % width;
        const cy = (i - cx) / width;
        if (cx < minX) minX = cx;
        if (cx > maxX) maxX = cx;
        if (cy < minY) minY = cy;
        if (cy > maxY) maxY = cy;

        if (cx > 0) stack.push(i - 1);
        if (cx < width - 1) stack.push(i + 1);
        if (cy > 0) stack.push(i - width);
        if (cy < height - 1) stack.push(i + width);
      }

      components.push({
        colorIndex: color,
        pixels,
        bbox: { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 },
      });
    }
  }

  return components;
}
